import api from "./axios";

export interface StudentClass {
    id: string;
    student_id: string;
    class_id: string;
    semester_id: string;
    enrollment_date: string;
    is_active: boolean;
    created_at: string;
    updated_at: string;
    student?: {
        id: string;
        student_id: string;
        nisn: string;
        name?: string;
        user?: {
            id: string;
            name: string;
            email: string;
        };
    };
    class?: {
        id: string;
        name: string;
        grade_level: string;
        major: string;
        capacity: number;
        is_active: boolean;
    };
    semester?: {
        id: string;
        name: string;
        start_date: string;
        end_date: string;
        is_active: boolean;
        academic_year?: {
            id: string;
            name: string;
            year: string;
            is_active: boolean;
        };
    };
    duration?: string;
    duration_days?: number;
    status_display?: string;
}

export interface CreateStudentClassData {
    student_id?: string;
    class_id?: string;
    semester_id?: string;
    enrollment_date?: string;
    is_active?: boolean;
}

export interface UpdateStudentClassData {
    student_id?: string;
    class_id?: string;
    semester_id?: string;
    enrollment_date?: string;
    is_active?: boolean;
}

export interface StudentClassFilters {
    search?: string;
    student_id?: string;
    class_id?: string;
    semester_id?: string;
    is_active?: boolean;
}

const studentClassApi = {
    // Get all student classes with pagination
    async getAll(
        params?: { per_page?: number; page?: number } & StudentClassFilters,
    ): Promise<{
        success: boolean;
        message: string;
        data: { items: StudentClass[]; meta: any };
    }> {
        const response = await api.get("/v1/student-classes", { params });
        return response.data;
    },

    // Get student class by ID
    async getById(id: string): Promise<{ data: StudentClass }> {
        const response = await api.get(`/v1/student-classes/${id}`);
        return response.data;
    },

    // Create new student class
    async create(
        data: CreateStudentClassData,
    ): Promise<{ success: boolean; message: string; data: StudentClass }> {
        const response = await api.post("/v1/student-classes", data);
        return response.data;
    },

    // Update student class
    async update(
        id: string,
        data: UpdateStudentClassData,
    ): Promise<{ success: boolean; message: string; data: StudentClass }> {
        const response = await api.put(`/v1/student-classes/${id}`, data);
        return response.data;
    },

    // Delete student class
    async delete(id: string): Promise<{ success: boolean; message: string }> {
        const response = await api.delete(`/v1/student-classes/${id}`);
        return response.data;
    },

    // Get student classes by student ID
    async getByStudent(studentId: string): Promise<{ data: StudentClass[] }> {
        const response = await api.get(
            `/v1/student-classes/by-student/${studentId}`,
        );
        return response.data;
    },

    // Get student classes by class ID
    async getByClass(classId: string): Promise<{ data: StudentClass[] }> {
        const response = await api.get(
            `/v1/student-classes/by-class/${classId}`,
        );
        return response.data;
    },

    // Get student classes by semester ID
    async getBySemester(semesterId: string): Promise<{ data: StudentClass[] }> {
        const response = await api.get(
            `/v1/student-classes/by-semester/${semesterId}`,
        );
        return response.data;
    },

    // Activate student class enrollment
    async activate(
        id: string,
    ): Promise<{ success: boolean; message: string; data: StudentClass }> {
        const response = await api.post(`/v1/student-classes/${id}/activate`);
        return response.data;
    },

    // Deactivate student class enrollment
    async deactivate(
        id: string,
    ): Promise<{ success: boolean; message: string; data: StudentClass }> {
        const response = await api.post(`/v1/student-classes/${id}/deactivate`);
        return response.data;
    },

    // Bulk create student class enrollments
    async bulkCreate(data: {
        class_id: string;
        semester_id: string;
        enrollment_date: string;
        is_active?: boolean;
        student_ids: string[];
    }): Promise<{
        success: boolean;
        message: string;
        data: {
            success_count: number;
            error_count: number;
            total_count: number;
            results: Array<{
                success: boolean;
                student_id: string;
                student_class?: StudentClass;
                error?: string;
            }>;
            errors: string[];
        };
    }> {
        const response = await api.post("/v1/student-classes/bulk", data);
        return response.data;
    },

    // Import student class enrollments from Excel/CSV file
    async import(file: File): Promise<{
        message: string;
        success: boolean;
        code: number;
        data: Array<{
            student_name: string;
            student_nisn: string;
            class_code: string;
            semester_code: string;
            enrollment_date: string;
            is_active: boolean;
        }>;
    }> {
        const formData = new FormData();
        formData.append("file", file);
        const response = await api.post(
            "/v1/student-classes/import",
            formData,
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        );
        return response.data;
    },
};

export default studentClassApi;
